import type { TypeView } from './evalsSite';

/**
 * evals.tldr.fm: eval types, their evals and runs, and spot checks, for Kingfisher admins only. Any hostname
 * whose first label is `evals` qualifies, so a test environment's name works too. Every other host
 * keeps the full app, where /admin/evals redirects the types this site hosts.
 */
export function isEvalsHost(hostname: string): boolean {
  return hostname.toLowerCase().split('.')[0] === 'evals';
}

// Each view of a type page but its evals, which are the page itself: /quality_scoring/slow-llm.
const VIEW_SLUGS: { [view in Exclude<TypeView, 'evals'>]: string } = {
  decision_model: 'decision-model', slow_llm: 'slow-llm', payloads: 'payloads',
};

/** The view a type page's path names after the type: its evals when it names none, null when it names no view. */
export function viewFromSlug(slug: string | undefined): TypeView | null {
  if (slug === undefined) return 'evals';
  return (Object.keys(VIEW_SLUGS) as (keyof typeof VIEW_SLUGS)[]).find(view => VIEW_SLUGS[view] === slug) ?? null;
}

/** Where things live on evals.tldr.fm: a type's page (at one of its views), one eval (by its UUID), one run, two runs of
 *  one sample side by side, and the review queue (of one type). */
export const evalsSitePaths = {
  type: (type: string, view: TypeView = 'evals') =>
    `/${encodeURIComponent(type)}${view === 'evals' ? '' : `/${VIEW_SLUGS[view]}`}`,
  evalPage: (evalUuid: string) => `/eval/${evalUuid}`,
  run: (runId: string) => `/runs/${runId}`,
  pair: (runId: string, otherId: string) => `/runs/${runId}/vs/${otherId}`,
  review: (type?: string) => type ? `/review/${encodeURIComponent(type)}` : '/review',
};

/** The evals.tldr.fm URL an /admin/evals page of a hosted type moves to (a case by its UUID). */
export function evalsSiteUrl(baseUrl: string, type: string, caseUuid?: string): string {
  return baseUrl + (caseUuid ? evalsSitePaths.evalPage(caseUuid) : evalsSitePaths.type(type));
}
